import { app } from "js/namespaces";
import { ActionPermissionsObject, PermissionActionType } from "common/interfaces";
import { Icon, MenuItem } from "@material-ui/core";
import { ThemeType } from "common/constants";
import UpgradeIcon from "js/Components/UpgradeIcon";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { ThemeThumbnail } from "js/editor/ThemeEditor/components/ThemeThumbnail";
import { Gap10, Gap30 } from "js/react/components/Gap";
import { ScrollBox } from "js/react/components/LayoutGrid";
import { SimpleLabel } from "js/react/components/SimpleLabel";
import { IconMenu } from "js/react/components/UiComponents";
import { $ } from "js/vendor";
import React, { Component, Fragment } from "react";
import { withTranslation } from "react-i18next";
import styled from "styled-components";
import FetchingClickShield from "../../../react/components/FetchingClickShield";

const logger = getLogger(LogGroup.THEMES);

const ThumbnailScrollBox = styled(ScrollBox)`
    padding: 0 20px 0px 20px;
    background: #f1f1f1;
`;

class ThemeThumbnailListBase extends Component {
    state = {
        teamThemes: [],
        userThemes: [],
        builtInThemes: [],
        fetching: true
    }

    async componentDidMount() {
        try {
            const teamThemes = await WorkspaceController.getSharedThemes(true, true);
            const userThemes = await WorkspaceController.getUserThemes(true);
            const builtInThemes = ds.builtInThemes.models;

            this.setState({
                teamThemes,
                userThemes,
                builtInThemes,
                fetching: false
            });
        } catch (err) {
            logger.error(err, "ThemeThumbnailList componentDidMount() failed");
        }
    }

    render() {
        const { t } = this.props;
        const {
            styles,
            selectedTheme,
            requireTeamTheme,
            onClick,
            onDoubleClick,
            width = "100%",
            pptTheme,
        } = this.props;
        const {
            teamThemes,
            userThemes,
            builtInThemes,
            fetching,
        } = this.state;

        const canUpgradeToUseTeamThemes = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.USE);

        return (
            <ThumbnailScrollBox id="theme-thumbnail-list" fillHeight fillWidth style={styles}>
                <FetchingClickShield visible={fetching} backgroundColor="white" />
                {!fetching && <>
                    {pptTheme && (
                        <Fragment>
                            <SimpleLabel>{t("POWERPOINT THEME")}</SimpleLabel>
                            <Gap10 />
                            <ThemeThumbnailGrid
                                t={t}
                                type={ThemeType.PPT_THEME}
                                themes={[pptTheme]}
                                width={width}
                                selectedTheme={selectedTheme}
                                onClick={theme => onClick(theme, ThemeType.TEAM)}
                                onDoubleClick={theme => onDoubleClick(theme, ThemeType.TEAM)}
                            />
                            <Gap30 />
                        </Fragment>
                    )}
                    {teamThemes.length > 0 && (
                        <Fragment>
                            <SimpleLabel>
                                {t("TEAM THEMES")}
                                {canUpgradeToUseTeamThemes && (
                                    <UpgradeIcon style={{ marginLeft: 10 }} />
                                )}
                            </SimpleLabel>
                            <Gap10 />
                            <ThemeThumbnailGrid
                                t={t}
                                type={ThemeType.TEAM}
                                themes={teamThemes}
                                width={width}
                                selectedTheme={selectedTheme}
                                onClick={theme => onClick(theme, ThemeType.TEAM)}
                                onDoubleClick={theme => onDoubleClick(theme, ThemeType.TEAM)}
                                disabled={canUpgradeToUseTeamThemes}
                            />
                            <Gap30 />
                        </Fragment>
                    )}

                    {!requireTeamTheme && userThemes.length > 0 && (
                        <Fragment>
                            <SimpleLabel>
                                {t("SAVED THEMES")}
                            </SimpleLabel>
                            <Gap10 />
                            <ThemeThumbnailGrid
                                t={t}
                                type={ThemeType.USER}
                                themes={userThemes}
                                width={width}
                                selectedTheme={selectedTheme}
                                onClick={theme => onClick(theme, ThemeType.USER)}
                                onDoubleClick={theme => onDoubleClick(theme, ThemeType.USER)}
                            />
                            <Gap30 />
                        </Fragment>
                    )}
                    {!requireTeamTheme && (
                        <Fragment>
                            <SimpleLabel>{t("BEAUTIFUL.AI THEMES")}</SimpleLabel>
                            <Gap10 />
                            <ThemeThumbnailGrid
                                t={t}
                                type={ThemeType.BUILT_IN}
                                themes={builtInThemes}
                                width={width}
                                selectedTheme={selectedTheme}
                                onClick={theme => onClick(theme, ThemeType.BUILT_IN)}
                                onDoubleClick={theme => onDoubleClick(theme, ThemeType.BUILT_IN)}
                            />
                        </Fragment>
                    )}
                </>}
            </ThumbnailScrollBox>
        );
    }
}

export const ThemeThumbnailList = withTranslation()(ThemeThumbnailListBase);

const ThemeThumbnailGridContainer = styled.div`
    position: relative;
    width: 100%;
    transition: 500ms;
`;

const ThemeThumbnailWrapper = styled.div.attrs(props => ({
    style: {
        ...props.bounds
    }
}))`
    position: absolute;
    transition: ${props => props.transition ? 500 : 0}ms;
    width: 200px;
    height: 180px;
`;

class ThemeThumbnailGrid extends Component {
    constructor() {
        super();
        this.ref = React.createRef();

        this.state = {
            width: null,
            transforms: null,
            transition: false
        };

        this.isUnmounted = false;
    }

    componentDidMount() {
        this.setState({ transition: true });

        $(window).on("resize.theme-thumbnail-grid", () => {
            if (!this.ref.current || this.isUnmounted) {
                return;
            }

            this.layout(this.ref.current.clientWidth);
        });
        // Wait a frame to ensure the ref has been setup, then layout
        window.requestAnimationFrame(() => {
            if (!this.ref.current || this.isUnmounted) {
                return;
            }

            this.layout(this.ref.current.clientWidth);
        });
    }

    componentWillUnmount() {
        this.isUnmounted = true;

        $(window).off(".theme-thumbnail-grid");
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.width !== prevProps.width) {
            this.layout(this.props.width);
        }
    }

    layout(width) {
        const { themes, maxColumnWidth = 250 } = this.props;

        if (!width || !themes.length) {
            return {};
        }

        const gap = 10;

        let columns = Math.max(3, Math.ceil(width / (maxColumnWidth)));

        if (app.isMobileOrTablet) {
            columns = 2;
        }

        let colWidth = (width - gap * (columns - 1)) / columns;
        let rowHeight = colWidth * 0.5625;

        if (!themes.at(0).get("isBuiltIn")) {
            rowHeight += 30;
        }

        let height = 0;
        let transforms = [];
        for (let i = 0; i < themes.length; i++) {
            let col = i % columns;
            let row = Math.floor(i / columns);

            let x = (colWidth + gap) * col;
            let y = (rowHeight + gap) * row;

            transforms.push({
                left: x,
                top: y,
                width: colWidth,
                height: rowHeight,
            });

            height = y + rowHeight + gap;
        }

        this.setState({ transforms, width, height });
    }

    getMenu = theme => {
        const { t } = this.props;
        switch (this.props.type) {
            case ThemeType.USER:
                return (
                    <IconMenu>
                        <MenuItem onClick={() => this.handleEdit(theme)}><Icon>edit</Icon>{t("Edit Theme...")}</MenuItem>
                        <MenuItem onClick={() => this.handleRename(theme)}><Icon>label</Icon>{t("Rename")}</MenuItem>
                        <MenuItem key="delete"
                            onClick={() => this.handleDelete(theme)}><Icon>delete</Icon>{t("Delete...")}</MenuItem>
                    </IconMenu>
                );
        }
    }

    render() {
        const { themes, selectedTheme, onClick, onDoubleClick, disabled } = this.props;
        const { transforms, height, transition } = this.state;

        return (
            <ThemeThumbnailGridContainer ref={this.ref} style={{ height, opacity: disabled ? 0.5 : 1 }}>
                {transforms && themes.map((theme, index) => {
                    return (
                        <ThemeThumbnailWrapper
                            id={`theme-thumbnail-${theme.id}`}
                            key={index}
                            bounds={transforms[index]}
                            onClick={() => !disabled && onClick(theme)}
                            onDoubleClick={() => !disabled && onDoubleClick(theme)}
                            transition={transition}
                        >
                            <ThemeThumbnail
                                item={theme}
                                selected={theme.id === selectedTheme?.id}
                            />
                        </ThemeThumbnailWrapper>
                    );
                })}
            </ThemeThumbnailGridContainer>
        );
    }
}
